import { EventOfflineDataError, validateOperation } from './operationSchema';
import { eventVaultExportSchema } from './exportSchema';
import { forgetOfflineScope } from './locators';
import { vaultStorage, type EncryptedValue, type VaultMetadata, type VaultRecord } from './storage';

export interface EventOfflineScope { userId: string; eventId: string }
export type EventOperationState = 'pending' | 'completed' | 'needs-review';
export interface EventOfflineOperation<T = unknown> {
  operationId: string;
  kind: string;
  payload: T;
  state: EventOperationState;
  capturedAt: string;
  sequence?: number;
  attempts: number;
  message?: string;
}
export interface ReplayResult { state: EventOperationState; message?: string }
export interface EventVaultExport {
  format: 'meridian-event-vault';
  version: 1;
  metadata: VaultMetadata;
  records: VaultRecord[];
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const activeVaults = new Set<EventOfflineVault>();
const localLocks = new Map<string, Promise<unknown>>();
const lockChannel = typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function'
  ? new window.BroadcastChannel('meridian-event-vault-lock') : null;

function lockLocalVaults(): void {
  for (const vault of activeVaults) vault.lock();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('meridian:event-vaults-locked'));
}
if (lockChannel) lockChannel.onmessage = () => lockLocalVaults();

function serialize(value: unknown): string {
  const json = JSON.stringify(value, (_key, item: unknown) => {
    if (item && typeof item === 'object' && !Array.isArray(item) && Object.getPrototypeOf(item) !== Object.prototype && Object.getPrototypeOf(item) !== null) {
      throw new Error('Offline payloads must be JSON. Encode badge images as base64 first.');
    }
    if (typeof item === 'function' || typeof item === 'symbol' || (typeof item === 'number' && !Number.isFinite(item))) throw new Error('Offline payload contains a value that cannot be preserved.');
    return item;
  });
  if (json === undefined) throw new Error('Offline payload is required.');
  return json;
}

async function exclusive<T>(name: string, work: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) return navigator.locks.request(name, work);
  const previous = localLocks.get(name) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(work);
  localLocks.set(name, next);
  try { return await next; } finally { if (localLocks.get(name) === next) localLocks.delete(name); }
}

async function wrappingKey(passphrase: string, salt: number[]): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt: new Uint8Array(salt), iterations: 600_000 }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

async function encrypt(key: CryptoKey, value: Uint8Array<ArrayBuffer>, context: string): Promise<EncryptedValue> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: encoder.encode(context) }, key, value);
  return { iv: Array.from(iv), ciphertext: Array.from(new Uint8Array(ciphertext)) };
}

async function decrypt(key: CryptoKey, value: EncryptedValue, context: string): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(value.iv), additionalData: encoder.encode(context) }, key, new Uint8Array(value.ciphertext));
}

export function lockEventVaults(): void {
  lockLocalVaults();
  lockChannel?.postMessage('lock');
}

const recordKind = (record: VaultRecord): unknown => {
  try { return (JSON.parse(record.id) as unknown[])[1]; } catch { return null; }
};

// Retention applies to vaults nobody reopens too. Without the passphrase only the plaintext expiry
// and settled markers are readable: the directory and synced actions go, and the whole vault goes
// once nothing unsynced is left. Unsynced actions stay for the rep to unlock, sync or export.
export async function pruneExpiredEventVaults(now = Date.now()): Promise<void> {
  const open = new Set([...activeVaults].filter(vault => vault.isUnlocked).map(vault => JSON.stringify([vault.identity.userId, vault.identity.eventId])));
  for (const metadata of await vaultStorage.allMetadata()) {
    if (Date.parse(metadata.expiresAt) > now || open.has(metadata.scope)) continue;
    await exclusive(`event-vault:${metadata.scope}`, async () => {
      const records = await vaultStorage.records(metadata.scope);
      const unsynced = records.filter(record => recordKind(record) === 'operation' && !record.settled);
      if (unsynced.length === 0) {
        await vaultStorage.clear(metadata.scope);
        const [userId, eventId] = JSON.parse(metadata.scope) as [string, string];
        forgetOfflineScope(userId, eventId);
        return;
      }
      await vaultStorage.remove(records.filter(record => recordKind(record) === 'snapshot' || (recordKind(record) === 'operation' && record.settled)).map(record => record.id));
    });
  }
}

export class EventOfflineVault {
  private readonly scope: string;
  private key: CryptoKey | null = null;
  private metadata: VaultMetadata | null = null;
  private generation = 0;

  readonly identity: EventOfflineScope;
  private readonly validatePayload?: (operation: EventOfflineOperation) => void;

  constructor(identity: EventOfflineScope, validatePayload?: (operation: EventOfflineOperation) => void) {
    this.validatePayload = validatePayload;
    this.identity = { ...identity };
    if (!identity.userId || !identity.eventId) throw new Error('A user and event are required for offline storage.');
    this.scope = JSON.stringify([identity.userId, identity.eventId]);
  }

  get isUnlocked(): boolean { return this.key !== null; }
  get isExpired(): boolean { return !this.metadata || Date.parse(this.metadata.expiresAt) <= Date.now(); }

  async isPrepared(): Promise<boolean> { return !!await vaultStorage.metadata(this.scope); }

  lock(): void {
    this.generation++;
    this.key = null;
    this.metadata = null;
    activeVaults.delete(this);
  }

  private credentials(allowExpired = false): { key: CryptoKey; metadata: VaultMetadata; generation: number } {
    if (!this.key || !this.metadata) throw new Error('Unlock this event’s offline data first.');
    if (!allowExpired && this.isExpired) throw new Error('Offline event data has expired. Synchronize or export pending activity before clearing it.');
    return { key: this.key, metadata: this.metadata, generation: this.generation };
  }

  private checkGeneration(generation: number): void {
    if (generation !== this.generation) throw new Error('Offline data was locked. Unlock the event to continue.');
  }

  private id(kind: string, id = ''): string { return JSON.stringify([this.scope, kind, id]); }
  private context(metadata: VaultMetadata, id: string): string { return JSON.stringify([metadata.scope, metadata.keyId, metadata.expiresAt, id]); }

  async prepare<T>(passphrase: string, expiresAt: string, snapshot: T): Promise<void> {
    activeVaults.add(this);
    if (passphrase.length < 12) throw new Error('Use an offline passphrase of at least 12 characters.');
    if (!Number.isFinite(Date.parse(expiresAt)) || Date.parse(expiresAt) <= Date.now()) throw new Error('Offline expiry must be in the future.');
    const generation = this.generation;
    await exclusive(`event-vault:${this.scope}`, async () => {
      if (await this.isPrepared()) throw new Error('This event already has offline data. Unlock it to preserve pending activity.');
      const salt = Array.from(crypto.getRandomValues(new Uint8Array(16)));
      const keyId = crypto.randomUUID();
      const rawKey = crypto.getRandomValues(new Uint8Array(32));
      const metadata: VaultMetadata = { scope: this.scope, keyId, version: 1, expiresAt: new Date(expiresAt).toISOString(), salt, wrappedKey: { iv: [], ciphertext: [] } };
      try {
        metadata.wrappedKey = await encrypt(await wrappingKey(passphrase, salt), rawKey, this.context(metadata, 'key'));
        const key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['encrypt', 'decrypt']);
        const id = this.id('snapshot');
        const value = await encrypt(key, encoder.encode(serialize(snapshot)), this.context(metadata, id));
        this.checkGeneration(generation);
        await vaultStorage.create(metadata, { id, scope: this.scope, value });
        this.checkGeneration(generation);
        this.key = key;
        this.metadata = metadata;
        activeVaults.add(this);
      } finally { rawKey.fill(0); }
    });
  }

  async unlock(passphrase: string): Promise<void> {
    activeVaults.add(this);
    const generation = this.generation;
    const metadata = await vaultStorage.metadata(this.scope);
    if (!metadata) throw new Error('Prepare this event for offline use while connected.');
    let rawKey: Uint8Array<ArrayBuffer>;
    try {
      rawKey = new Uint8Array(await decrypt(await wrappingKey(passphrase, metadata.salt), metadata.wrappedKey, this.context(metadata, 'key')));
    } catch { throw new Error('The offline passphrase is incorrect or this event’s data is damaged.'); }
    try {
      const key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['encrypt', 'decrypt']);
      this.checkGeneration(generation);
      this.key = key;
      this.metadata = metadata;
      activeVaults.add(this);
    } finally { rawKey.fill(0); }
  }

  private async read<T>(record: VaultRecord, allowExpired = false): Promise<T> {
    const { key, metadata, generation } = this.credentials(allowExpired);
    const data = await decrypt(key, record.value, this.context(metadata, record.id));
    this.checkGeneration(generation);
    let value: unknown;
    try { value = JSON.parse(decoder.decode(data)); } catch { throw new EventOfflineDataError(); }
    this.validateRecord(record.id, value);
    return value as T;
  }

  private validateRecord(recordId: string, value: unknown): void {
    const id = this.recordIdentity(recordId);
    if (id[1] === 'operation') { validateOperation(value, id[2]); this.validatePayload?.(value); }
    if (id[1] === 'recovery' && (!value || typeof value !== 'object' || Array.isArray(value) || Object.values(value).some(alias => typeof alias !== 'string'))) throw new EventOfflineDataError();
  }
  private recordIdentity(recordId: string): [string, string, string] {
    let id: unknown;
    try { id = JSON.parse(recordId); } catch { throw new EventOfflineDataError(); }
    if (!Array.isArray(id) || id.length !== 3 || id[0] !== this.scope || !['snapshot', 'operation', 'recovery'].includes(id[1]) || typeof id[2] !== 'string' || (id[1] === 'operation' ? !id[2] : id[2] !== '')) throw new EventOfflineDataError();
    return id as [string, string, string];
  }

  async readSnapshot<T>(): Promise<T | undefined> {
    this.credentials();
    const record = await vaultStorage.get(this.id('snapshot'));
    return record ? this.read<T>(record) : undefined;
  }

  private async write<T>(id: string, payload: T, allowExpired = false, insertOnly = false): Promise<void> {
    const { key, metadata, generation } = this.credentials(allowExpired);
    this.validateRecord(id, payload);
    const value = await encrypt(key, encoder.encode(serialize(payload)), this.context(metadata, id));
    this.checkGeneration(generation);
    const settled = this.recordIdentity(id)[1] === 'operation' && (payload as EventOfflineOperation).state === 'completed';
    await vaultStorage.put({ id, scope: this.scope, value, ...(settled ? { settled } : {}) }, insertOnly, metadata.keyId);
    this.checkGeneration(generation);
  }

  async updateSnapshot<T>(snapshot: T): Promise<void> { await this.write(this.id('snapshot'), snapshot); }

  async readRecoveryAliases(): Promise<Record<string, string>> {
    this.credentials(true);
    const record = await vaultStorage.get(this.id('recovery'));
    return record ? this.read<Record<string, string>>(record, true) : {};
  }
  async writeRecoveryAliases(aliases: Record<string, string>): Promise<void> {
    await this.write(this.id('recovery'), aliases, true);
  }

  async enqueue<T>(input: { operationId?: string; kind: string; payload: T; capturedAt?: string }): Promise<EventOfflineOperation<T>> {
    this.credentials();
    if (!input.kind.trim()) throw new Error('An operation kind is required.');
    const capturedAt = input.capturedAt ?? new Date().toISOString();
    if (!Number.isFinite(Date.parse(capturedAt))) throw new Error('A valid capture timestamp is required.');
    const operationId = input.operationId ?? crypto.randomUUID();
    const operation: EventOfflineOperation<T> = { ...input, operationId, state: 'pending', capturedAt, attempts: 0 };
    return exclusive(`event-vault:${this.scope}`, async () => {
      const id = this.id('operation', operationId);
      const existing = await vaultStorage.get(id);
      if (existing) {
        const previous = await this.read<EventOfflineOperation<T>>(existing, true);
        if (previous.kind !== input.kind || JSON.stringify(previous.payload) !== JSON.stringify(input.payload)) throw new Error('This operation ID is already used by a different action.');
        return previous;
      }
      operation.sequence = (await this.listOperations()).reduce((maximum, item) => Math.max(maximum, item.sequence ?? 0), 0) + 1;
      await this.write(id, operation, false, true);
      return operation;
    });
  }

  async listOperations<T = unknown>(): Promise<EventOfflineOperation<T>[]> {
    this.credentials(true);
    const records = (await vaultStorage.records(this.scope)).filter(record => this.recordIdentity(record.id)[1] === 'operation');
    const operations = await Promise.all(records.map(record => this.read<EventOfflineOperation<T>>(record, true)));
    return operations.sort((a, b) => a.sequence !== undefined && b.sequence !== undefined ? a.sequence - b.sequence : a.sequence !== undefined ? 1 : b.sequence !== undefined ? -1 : a.capturedAt.localeCompare(b.capturedAt) || a.operationId.localeCompare(b.operationId));
  }

  async replay<T = unknown>(send: (operation: EventOfflineOperation<T>) => Promise<ReplayResult>, dependencies: (operation: EventOfflineOperation<T>) => readonly string[] = () => []): Promise<EventOfflineOperation<T>[]> {
    return exclusive(`event-vault-replay:${this.scope}`, async () => {
      const generation = this.credentials(true).generation;
      const operations = await this.listOperations<T>();
      const byId = new Map(operations.map(operation => [operation.operationId, operation]));
      const ordered: EventOfflineOperation<T>[] = [];
      const visited = new Set<string>();
      const visiting = new Set<string>();
      const visit = (operation: EventOfflineOperation<T>) => {
        if (visited.has(operation.operationId)) return;
        if (visiting.has(operation.operationId)) throw new Error('Offline actions contain a circular dependency. Export them for recovery.');
        visiting.add(operation.operationId);
        for (const id of dependencies(operation)) {
          const dependency = byId.get(id);
          if (dependency && dependency !== operation) visit(dependency);
        }
        visiting.delete(operation.operationId);
        visited.add(operation.operationId);
        ordered.push(operation);
      };
      operations.forEach(visit);
      for (const operation of ordered) {
        this.checkGeneration(generation);
        if (operation.state !== 'pending') continue;
        operation.attempts++;
        await this.write(this.id('operation', operation.operationId), operation, true);
        let result: ReplayResult;
        try {
          const unresolved = dependencies(operation).some(id => id !== operation.operationId && byId.has(id) && byId.get(id)?.state !== 'completed');
          result = unresolved ? { state: 'needs-review', message: 'Resolve the earlier contact action before retrying this action.' } : await send(operation);
        }
        catch { result = { state: 'pending', message: 'Not synchronized. Reconnect and retry this action.' }; }
        this.checkGeneration(generation);
        Object.assign(operation, result);
        await this.write(this.id('operation', operation.operationId), { ...operation, ...result }, true);
        if (result.state === 'pending') break;
      }
      return this.listOperations<T>();
    });
  }

  async retry(operationId: string): Promise<void> {
    await exclusive(`event-vault:${this.scope}`, async () => {
      const record = await vaultStorage.get(this.id('operation', operationId));
      if (!record) throw new Error('Offline action was not found.');
      const operation = await this.read<EventOfflineOperation>(record, true);
      if (operation.state === 'completed') return;
      await this.write(record.id, { ...operation, state: 'pending', message: undefined }, true);
    });
  }

  // A rejected action the rep decides not to keep (for example a registration the session had no room
  // for). Pending actions are never discarded: they may already have reached Meridian.
  async discard(operationId: string): Promise<void> {
    await exclusive(`event-vault:${this.scope}`, async () => {
      const record = await vaultStorage.get(this.id('operation', operationId));
      if (!record) throw new Error('Offline action was not found.');
      const operation = await this.read<EventOfflineOperation>(record, true);
      if (operation.state !== 'needs-review') throw new Error('Only actions that need review can be discarded.');
      await vaultStorage.remove([record.id]);
    });
  }

  async restoreEncrypted(passphrase: string, backup: unknown): Promise<void> {
    activeVaults.add(this);
    const parsed = eventVaultExportSchema.parse(backup);
    if (parsed.metadata.scope !== this.scope || parsed.records.some(record => record.scope !== this.scope)) throw new Error('This recovery file belongs to a different user or event.');
    const generation = this.generation;
    await exclusive(`event-vault:${this.scope}`, async () => {
      if (await this.isPrepared()) throw new Error('Offline data already exists. Restore into an empty event vault to avoid losing activity.');
      const metadata = parsed.metadata;
      let rawKey: Uint8Array<ArrayBuffer>;
      try { rawKey = new Uint8Array(await decrypt(await wrappingKey(passphrase, metadata.salt), metadata.wrappedKey, this.context(metadata, 'key'))); }
      catch { throw new Error('The recovery passphrase is incorrect or the file is damaged.'); }
      try {
        const key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['encrypt', 'decrypt']);
        const identities = new Set<string>();
        for (const record of parsed.records) {
          if (identities.has(record.id)) throw new EventOfflineDataError();
          identities.add(record.id);
          let value: unknown;
          try { value = JSON.parse(decoder.decode(await decrypt(key, record.value, this.context(metadata, record.id)))); }
          catch { throw new EventOfflineDataError(); }
          this.validateRecord(record.id, value);
        }
        this.checkGeneration(generation);
        await vaultStorage.restore(metadata, parsed.records);
        this.checkGeneration(generation);
        this.key = key;
        this.metadata = metadata;
        activeVaults.add(this);
      } finally { rawKey.fill(0); }
    });
  }

  async exportEncrypted(): Promise<EventVaultExport> {
    const metadata = await vaultStorage.metadata(this.scope);
    if (!metadata) throw new Error('No offline data is available to export.');
    return { format: 'meridian-event-vault', version: 1, metadata, records: await vaultStorage.records(this.scope) };
  }

  async pruneExpired(): Promise<{ expired: boolean; preservedPending: number }> {
    return exclusive(`event-vault:${this.scope}`, async () => {
      this.credentials(true);
      if (!this.isExpired) return { expired: false, preservedPending: 0 };
      const operations = await this.listOperations();
      const pending = operations.filter(operation => operation.state !== 'completed');
      if (pending.length) {
        await vaultStorage.remove([this.id('snapshot'), ...operations.filter(operation => operation.state === 'completed').map(operation => this.id('operation', operation.operationId))]);
      } else {
        await vaultStorage.clear(this.scope);
        this.lock();
      }
      return { expired: true, preservedPending: pending.length };
    });
  }

  // For a mistyped or forgotten passphrase: without the key only the plaintext settled markers are
  // readable, so the vault goes only when every action on it has already synced.
  async forgetLocked(): Promise<void> {
    await exclusive(`event-vault:${this.scope}`, async () => {
      const records = await vaultStorage.records(this.scope);
      if (records.some(record => recordKind(record) === 'operation' && !record.settled)) throw new Error('This device holds actions that have not synced. Unlock to sync them, or export the encrypted recovery file, before removing offline data.');
      await vaultStorage.clear(this.scope);
      this.lock();
    });
  }

  async clear(): Promise<void> {
    await exclusive(`event-vault:${this.scope}`, async () => {
      if ((await this.listOperations()).some(operation => operation.state !== 'completed')) throw new Error('Pending activity must be synchronized before this event can be cleared. Export an encrypted recovery copy if needed.');
      await vaultStorage.clear(this.scope);
      this.lock();
    });
  }
}
